import { AppointmentCancellationReason } from "appointment-experience-client";
import type { AppointmentOutcome } from "./AppointmentOverlay";

export type ExitOutcome = Exclude<AppointmentOutcome, "completed">;

const EXIT_OPTIONS: {
  outcome: ExitOutcome;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    outcome: "exit",
    label: "Return to planned",
    description: "Opened by mistake. It can be started again later.",
    icon: "undo",
  },
  {
    outcome: "no-show",
    label: "No show",
    description: "The patient didn't attend.",
    icon: "person_off",
  },
  {
    outcome: "canceled",
    label: "Cancel appointment",
    description: "It won't go ahead. You'll choose a reason.",
    icon: "event_busy",
  },
];

const CONFIRM: Record<
  ExitOutcome,
  { text: string; note: string; action: string; className: string }
> = {
  exit: {
    text: "Exit this appointment?",
    note: "The status will return to planned.",
    action: "Yes, return to planned",
    className: "appointment-overlay__footer-btn--quiet-solid",
  },
  "no-show": {
    text: "Mark this appointment as a no show?",
    note: "This cannot be undone.",
    action: "Yes, no show",
    className: "appointment-overlay__footer-btn--warning-solid",
  },
  canceled: {
    text: "To confirm cancellation choose a reason below.",
    note: "This cannot be undone.",
    action: "Yes, cancel appointment",
    className: "appointment-overlay__footer-btn--danger-solid",
  },
};

type AppointmentOverlayExitChooserProps = {
  /** The option being confirmed, or null while choosing one. */
  outcome: ExitOutcome | null;
  finishing: boolean;
  cancellationReasons: AppointmentCancellationReason[] | null;
  cancelReason: string;
  onCancelReasonChange: (reasonId: string) => void;
  onChoose: (outcome: ExitOutcome) => void;
  /** Back from the options closes the chooser; back from a confirmation returns to them. */
  onBack: () => void;
  onConfirm: () => void;
};

const AppointmentOverlayExitChooser = ({
  outcome,
  finishing,
  cancellationReasons,
  cancelReason,
  onCancelReasonChange,
  onChoose,
  onBack,
  onConfirm,
}: AppointmentOverlayExitChooserProps) => {
  const option = EXIT_OPTIONS.find((o) => o.outcome === outcome);
  const confirm = outcome ? CONFIRM[outcome] : null;

  return (
    <div
      className="appointment-overlay__journey-chooser appointment-overlay__exit-chooser"
      onKeyDown={(event) => {
        if (event.key === "Escape" && !finishing) {
          event.stopPropagation();
          onBack();
        }
      }}
    >
      <div className="appointment-overlay__body-header">
        <span className="appointment-overlay__body-title">
          {option?.label ?? "Exit appointment"}
        </span>
        <button
          type="button"
          className="appointment-overlay__journey-link"
          onClick={onBack}
          disabled={finishing}
        >
          Back
        </button>
      </div>

      {confirm ? (
        <div className="appointment-overlay__exit-confirm">
          <div className="appointment-overlay__exit-content">
            <p
              className="appointment-overlay__confirm-text"
              role="alert"
              id="appointment-overlay-exit-confirm"
            >
              <span className="appointment-overlay__confirm-question">
                {confirm.text}
              </span>{" "}
              <span className="appointment-overlay__confirm-note">{confirm.note}</span>
            </p>
            {outcome === "canceled" && (
              <fieldset
                className="appointment-overlay__cancel-reasons"
                disabled={finishing}
              >
                {/* The question above says it; the legend names the group for screen readers. */}
                <legend className="visually-hidden">Cancellation reason</legend>
                {!cancellationReasons ? (
                  <span className="appointment-overlay__cancel-reasons-note">
                    Loading reasons...
                  </span>
                ) : cancellationReasons.length === 0 ? (
                  <span className="appointment-overlay__cancel-reasons-note">
                    Couldn&apos;t load the cancellation reasons.
                  </span>
                ) : (
                  cancellationReasons.map((reason, index) => (
                    <label className="appointment-overlay__cancel-reason" key={reason.id}>
                      <input
                        type="radio"
                        className="form-check-input"
                        name="appointment-overlay-cancel-reason"
                        value={reason.id}
                        checked={cancelReason === reason.id}
                        onChange={() => onCancelReasonChange(reason.id)}
                        data-autofocus={index === 0 ? true : undefined}
                      />
                      <span>{reason.reasonName}</span>
                    </label>
                  ))
                )}
              </fieldset>
            )}
          </div>
          <div className="appointment-overlay__exit-actions">
            <button
              type="button"
              className={`appointment-overlay__footer-btn appointment-overlay__footer-btn--compact ${confirm.className}`}
              onClick={onConfirm}
              disabled={finishing || (outcome === "canceled" && !cancelReason)}
              aria-describedby="appointment-overlay-exit-confirm"
              data-autofocus={outcome === "canceled" ? undefined : true}
            >
              {finishing ? "Saving..." : confirm.action}
            </button>
          </div>
        </div>
      ) : (
        <ul
          className="appointment-overlay__choices"
          aria-label="Ways to exit the appointment"
        >
          {EXIT_OPTIONS.map((exitOption, index) => (
            <li key={exitOption.outcome}>
              <button
                type="button"
                className={`appointment-overlay__choice appointment-overlay__exit-option appointment-overlay__exit-option--${exitOption.outcome}`}
                onClick={() => onChoose(exitOption.outcome)}
                aria-labelledby={`appointment-overlay-exit-${exitOption.outcome}-name`}
                aria-describedby={`appointment-overlay-exit-${exitOption.outcome}`}
                data-autofocus={index === 0 ? true : undefined}
              >
                <span
                  className="appointment-overlay__exit-icon material-symbols-outlined"
                  aria-hidden="true"
                >
                  {exitOption.icon}
                </span>
                <span className="appointment-overlay__choice-text">
                  <span
                    className="appointment-overlay__choice-name"
                    id={`appointment-overlay-exit-${exitOption.outcome}-name`}
                  >
                    {exitOption.label}
                  </span>
                  <span
                    className="appointment-overlay__choice-subtitle"
                    id={`appointment-overlay-exit-${exitOption.outcome}`}
                  >
                    {exitOption.description}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AppointmentOverlayExitChooser;
